import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

/**
 * A column container that stacks children vertically with horizontal
 * drag handles between adjacent children, letting the user resize each
 * section's height. The LAST section gets `flex: 1` and absorbs the
 * remaining space — every other section has a controlled height in px.
 *
 * Heights are persisted in `localStorage` under `storageKey` so the
 * layout sticks across reloads.
 *
 * Used by the BuilderScreen to make the left column (palette / screens
 * / view-tree) and the right column (properties / theme / content /
 * build) resizable per the author's working preference. A property-
 * heavy session can grow the property panel; a layout-heavy session
 * can grow the view tree; etc.
 *
 * Drag model: capture (startY, startHeight) on mousedown, update with
 * absolute delta on mousemove. Avoids accumulation drift you'd get if
 * we treated each move as a relative delta.
 */
export interface VerticalSplitSection {
  /** Stable key for React reconciliation. */
  key: string;
  /** Default height in px. Used on first mount when no localStorage entry exists. */
  defaultHeight: number;
  /** Lower bound for the section's height. Defaults to 80. */
  minHeight?: number;
  children: React.ReactNode;
}

export interface VerticalSplitProps {
  sections: VerticalSplitSection[];
  /**
   * localStorage key for persisting heights. The stored value is the
   * heights array of length `sections.length - 1` (the last section is
   * flex-grown so its height isn't stored).
   */
  storageKey: string;
}

const MIN_FALLBACK = 80;

function loadHeights(key: string, sections: VerticalSplitSection[]): number[] {
  const defaults = sections.slice(0, -1).map((s) => s.defaultHeight);
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length !== defaults.length) return defaults;
    return parsed.map((v, i) => {
      if (typeof v !== 'number' || !Number.isFinite(v)) return defaults[i];
      const min = sections[i].minHeight ?? MIN_FALLBACK;
      return Math.max(min, v);
    });
  } catch {
    return defaults;
  }
}

export function VerticalSplit({ sections, storageKey }: VerticalSplitProps) {
  const [heights, setHeights] = useState<number[]>(() => loadHeights(storageKey, sections));

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(heights));
  }, [storageKey, heights]);

  // Drag state lives in a ref — we don't need a render cycle on every
  // mousemove tick.
  const dragRef = useRef<{ index: number; startY: number; startHeight: number } | null>(null);

  const handleMove = useCallback(
    (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dy = e.clientY - drag.startY;
      const min = sections[drag.index]?.minHeight ?? MIN_FALLBACK;
      setHeights((prev) => {
        const next = [...prev];
        next[drag.index] = Math.max(min, drag.startHeight + dy);
        return next;
      });
    },
    [sections],
  );

  const stopDrag = useCallback(() => {
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', stopDrag);
  }, [handleMove]);

  // Defensive cleanup if component unmounts mid-drag.
  useEffect(() => stopDrag, [stopDrag]);

  const startDrag = (index: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { index, startY: e.clientY, startHeight: heights[index] ?? sections[index].defaultHeight };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stopDrag);
  };

  return (
    <div style={styles.container}>
      {sections.map((section, i) => {
        const isLast = i === sections.length - 1;
        const minHeight = section.minHeight ?? MIN_FALLBACK;
        return (
          <Fragment key={section.key}>
            <div
              style={
                isLast
                  ? { ...styles.section, flex: 1, minHeight }
                  : { ...styles.section, height: heights[i], flexShrink: 0, minHeight }
              }
            >
              {section.children}
            </div>
            {!isLast && (
              <div
                onMouseDown={startDrag(i)}
                style={styles.handle}
                role="separator"
                aria-orientation="horizontal"
                title="Drag to resize"
              >
                <div style={styles.grip} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  section: {
    overflow: 'auto',
  },
  handle: {
    height: 6,
    flexShrink: 0,
    cursor: 'row-resize',
    backgroundColor: '#E2E8F0',
    position: 'relative',
  },
  grip: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: 2,
    width: 28,
    borderRadius: 1,
    backgroundColor: '#94A3B8',
    pointerEvents: 'none',
  },
};
